/* eslint-disable */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { AppThunk } from '../store'
import { HTTP } from '../lib/axios'
import {
  calculateStatDifference,
  formatHeatMapSeries,
  formatLineChartSeries,
  formatPieChart,
  formatTopContacts,
  groupSMSByContactNumber,
  parseData,
  splitMinutes
} from 'src/utils/analytics'
import {
  GroupedSMS,
  SMSContactSum,
  SMSDataInterface,
  UserCommunicationsVolume
} from '../types/sms'
import { DateTime } from 'luxon'
import { CallSummary } from 'src/types/call'
import { PieChart } from 'src/types/analytics'

export interface AnalyticsState {
  groupedSMS: GroupedSMS
  contactSMS: SMSContactSum
  contactSMSLoaded: boolean
  userSMS: SMSContactSum
  userSMSLoaded: boolean
  numberSMS: SMSContactSum
  contactAverageChar: {
    inbound: number
    outbound: number
  }
  userAverageChar: { inbound: number; outbound: number }
  userCommunicationsVolume: any
  userCommunicationsVolumeLoaded: boolean
  accountCommunicationsVolume: any
  accountCommunicationsVolumeLoaded: boolean
  accountCallStatistics: {
    loading: boolean
    start: string
    end: string
    callDuration: {
      average: Duration
      total: Duration
      shortest: Duration
      longest: Duration
    }
    topNumber: {
      number: string
      totalCalls: number
      totalTime: Duration
    }
    counts: {
      calls: number
      inbound: number
      outbound: number
      abandoned: number
      missed: number
      desktop: number
      mobile: number
    }
    data: Array<CallSummary>
    topContacts: Array<{
      cells: { [key: string]: string | number }
      additionalInfo: {
        [key: string]: string | number
      }
    }>
    lineChartSeries: {
      categories: Array<string>
      series: Array<{ name: string; data: Array<any> }>
      interval: 'daily' | 'hourly'
    }
    pieChart: {
      devices: PieChart
      external: PieChart
      internal: PieChart
    }
  }
  priorIntervalAccountCallStatistics: {
    loading: boolean
    total: {
      calls: number
      inbound: number
      outbound: number
      abandoned: number
      missed: number
      desktop: number
      mobile: number
    }
    data: Array<CallSummary>
  }
  todaysAccountCallStatistics: {
    loading: boolean
    data: Array<CallSummary>
    busiestHour: string
    start: string
    end: string
  }
  monthlyAccountCallStatistics: {
    loading: boolean
    start: string
    end: string
    data: Array<CallSummary>
    heatMapSeries: Array<any>
  }
  accountCallStatisticsDifference: {
    [key: string]: {
      direction: '-' | '+' | ''
      value: number
    }
  }
  errors: string
  loading: boolean
  numberFilter: string[]
}

export const initialState: AnalyticsState = {
  groupedSMS: {},
  contactSMS: {},
  contactSMSLoaded: false,
  userSMS: {},
  userSMSLoaded: false,
  numberSMS: {},
  contactAverageChar: { inbound: 0, outbound: 0 },
  userAverageChar: { inbound: 0, outbound: 0 },
  userCommunicationsVolume: {
    series: [
      {
        data: [0],
        name: 'Texts'
      }
    ],
    xaxis: {
      dataPoints: [0]
    }
  },
  userCommunicationsVolumeLoaded: false,
  accountCommunicationsVolume: {
    series: [
      {
        data: [0],
        name: 'Texts'
      }
    ],
    xaxis: {
      dataPoints: [0]
    }
  },
  accountCommunicationsVolumeLoaded: false,
  accountCallStatistics: {
    loading: false,
    start: '',
    end: '',
    callDuration: {
      total: { hours: 0, minutes: 0, seconds: 0 },
      average: { hours: 0, minutes: 0, seconds: 0 },
      longest: { hours: 0, minutes: 0, seconds: 0 },
      shortest: { hours: 0, minutes: 0, seconds: 0 }
    },
    counts: {
      calls: 0,
      inbound: 0,
      outbound: 0,
      abandoned: 0,
      missed: 0,
      desktop: 0,
      mobile: 0
    },
    data: [],
    topNumber: {
      number: '',
      totalCalls: 0,
      totalTime: { hours: 0, minutes: 0, seconds: 0 }
    },
    topContacts: [],
    lineChartSeries: {
      categories: [],
      series: [{ name: '', data: [] }],
      interval: 'daily'
    },
    pieChart: {
      devices: {
        categories: [],
        series: []
      },
      external: {
        categories: [],
        series: []
      },
      internal: {
        categories: [],
        series: []
      }
    }
  },
  priorIntervalAccountCallStatistics: {
    loading: false,
    total: {
      calls: 0,
      inbound: 0,
      outbound: 0,
      abandoned: 0,
      missed: 0,
      desktop: 0,
      mobile: 0
    },
    data: []
  },
  todaysAccountCallStatistics: {
    loading: false,
    data: [],
    busiestHour: '',
    start: '0',
    end: '0'
  },
  monthlyAccountCallStatistics: {
    loading: false,
    start: '0',
    end: '0',
    data: [],
    heatMapSeries: []
  },
  accountCallStatisticsDifference: {
    calls: {
      direction: '',
      value: 0
    },
    inbound: {
      direction: '',
      value: 0
    },
    outbound: {
      direction: '',
      value: 0
    },
    abandoned: {
      direction: '',
      value: 0
    },
    missed: {
      direction: '',
      value: 0
    },
    desktop: {
      direction: '',
      value: 0
    },
    mobile: {
      direction: '',
      value: 0
    }
  },
  loading: false,
  errors: '',
  numberFilter: []
}

const formatContactSMS = (SMSData: Array<any>) => {
  //add internal and external number as fields
  for (let i = 0; i < SMSData.length; i++) {
    const currentSMS = SMSData[i]

    const inbound = currentSMS.Direction === 'inbound'
    if (inbound) {
      currentSMS.external = currentSMS.fromNumber
      currentSMS.internal = currentSMS.toNumber
    } else {
      currentSMS.external = currentSMS.toNumber
      currentSMS.internal = currentSMS.fromNumber
    }
  }
  const contactSMSCount = {}
  //sum them up
  for (let i = 0; i < SMSData.length; i++) {
    const currentSMS = SMSData[i]
    const inbound = currentSMS.Direction === 'inbound'
    const { external } = currentSMS

    //external number is the client's number
    if (!(external in contactSMSCount)) {
      if (inbound) {
        contactSMSCount[external] = {
          SMSReceived: 1,
          SMSSent: 0,
          attachments: 0
        }
      } else {
        contactSMSCount[external] = {
          SMSReceived: 0,
          SMSSent: 1,
          attachments: 0
        }
      }
    } else if (inbound) {
      contactSMSCount[external] = {
        ...contactSMSCount[external],
        SMSReceived: contactSMSCount[external].SMSReceived + 1
      }
    } else {
      contactSMSCount[external] = {
        ...contactSMSCount[external],
        SMSSent: contactSMSCount[external].SMSSent + 1
      }
    }

    if (currentSMS.attachment === 'true') {
      contactSMSCount[external] = {
        ...contactSMSCount[external],
        attachments: contactSMSCount[external].attachments + 1
      }
    }
  }

  return contactSMSCount
}

const formatUserSMS = (SMSData: Array<any>) => {
  //add internal and external number
  for (let i = 0; i < SMSData.length; i++) {
    const currentSMS = SMSData[i]
    const inbound = currentSMS.Direction === 'inbound'

    if (inbound) {
      currentSMS.external = currentSMS.fromNumber
      currentSMS.internal = currentSMS.toNumber
    } else {
      currentSMS.external = currentSMS.toNumber
      currentSMS.internal = currentSMS.fromNumber
    }
  }

  const userSMSCount = {}

  for (let i = 0; i < SMSData.length; i++) {
    const currentSMS = SMSData[i]
    const { userName } = currentSMS
    const inbound = currentSMS.Direction === 'inbound'

    if (!(userName in userSMSCount)) {
      if (inbound) {
        userSMSCount[userName] = {
          SMSReceived: 1,
          SMSSent: 0,
          attachments: 0
        }
      } else {
        userSMSCount[userName] = {
          SMSReceived: 0,
          SMSSent: 1,
          attachments: 0
        }
      }
    } else if (inbound) {
      userSMSCount[userName] = {
        ...userSMSCount[userName],
        SMSReceived: userSMSCount[userName].SMSReceived + 1
      }
    } else {
      userSMSCount[userName] = {
        ...userSMSCount[userName],
        SMSSent: userSMSCount[userName].SMSSent + 1
      }
    }

    if (currentSMS.attachment === 'true') {
      userSMSCount[userName] = {
        ...userSMSCount[userName],
        attachments: userSMSCount[userName].attachments + 1
      }
    }
  }

  return userSMSCount
}

const formatPhoneSMS = (SMSData: Array<any>) => {
  //add internal and external number
  for (let i = 0; i < SMSData.length; i++) {
    const currentSMS = SMSData[i]
    const inbound = currentSMS.Direction === 'inbound'

    if (inbound) {
      currentSMS.external = currentSMS.fromNumber
      currentSMS.internal = currentSMS.toNumber
    } else {
      currentSMS.external = currentSMS.toNumber
      currentSMS.internal = currentSMS.fromNumber
    }
  }

  const phoneSMSCount = {}

  for (let i = 0; i < SMSData.length; i++) {
    const currentSMS = SMSData[i]
    const { internal } = currentSMS
    const inbound = currentSMS.Direction === 'inbound'

    if (!(internal in phoneSMSCount)) {
      if (inbound) {
        phoneSMSCount[internal] = {
          SMSReceived: 1,
          SMSSent: 0,
          attachments: 0
        }
      } else {
        phoneSMSCount[internal] = {
          SMSReceived: 0,
          SMSSent: 1,
          attachments: 0
        }
      }
    } else if (inbound) {
      phoneSMSCount[internal] = {
        ...phoneSMSCount[internal],
        SMSReceived: phoneSMSCount[internal].SMSReceived + 1
      }
    } else {
      phoneSMSCount[internal] = {
        ...phoneSMSCount[internal],
        SMSSent: phoneSMSCount[internal].SMSSent + 1
      }
    }

    if (currentSMS.attachment === 'true') {
      phoneSMSCount[internal] = {
        ...phoneSMSCount[internal],
        attachments: phoneSMSCount[internal].attachments + 1
      }
    }
  }

  return phoneSMSCount
}

const getAverage = (SMSData: Array<any>) => {
  let totalInboundChars: number = 0
  let totalOutboundChars: number = 0
  let totalInboundSMS: number = 0
  let totalOutboundSMS: number = 0

  for (let i = 0; i < SMSData.length; i++) {
    //attachements have 0 chars
    if (SMSData[i].attachment === 'true') continue

    if (SMSData[i].Direction === 'inbound') {
      totalInboundSMS++
      totalInboundChars += Number(SMSData[i]['measure_value::double'])
    } else {
      totalOutboundSMS++
      totalOutboundChars += Number(SMSData[i]['measure_value::double'])
    }
  }

  //round all results
  const inboundAverageChars = parseFloat(
    (totalInboundChars / totalInboundSMS).toFixed(1)
  )
  const outboundAverageChars = parseFloat(
    (totalOutboundChars / totalOutboundSMS).toFixed(1)
  )

  return {
    inbound: inboundAverageChars ? inboundAverageChars : 0,
    outbound: outboundAverageChars ? outboundAverageChars : 0
  }
}

const formatCommunicationsChartData = (
  communicationsVolume: UserCommunicationsVolume[]
) => {
  const callsMap = new Map()

  communicationsVolume.forEach((msg) => {
    let { interval_timestamp, num } = msg

    let date = DateTime.fromSQL(interval_timestamp, {
      zone: 'utc'
    })

    const epochTime = date.toMillis()

    if (!callsMap.has(epochTime)) {
      callsMap.set(epochTime, Number(num))
    } else {
      callsMap.set(epochTime, callsMap.get(epochTime) + Number(num))
    }
  })

  const xValues = Array.from(callsMap.keys())
  const yValues = Array.from(callsMap.values())

  return {
    series: [
      {
        data: yValues,
        name: 'Texts'
      }
    ],
    xaxis: {
      dataPoints: xValues
    }
  }
}

export const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setLoading: (state, { payload }) => {
      state.loading = payload
    },

    setErrors: (state, { payload }: PayloadAction<string>) => {
      state.errors = payload
    },

    setUserSMS: (state, { payload }: PayloadAction<Array<any>>) => {
      // numbers to filter on
      const numbers = state.numberFilter

      // uses accountId to query
      state.userSMS = formatUserSMS(payload)
      state.userAverageChar = getAverage(payload)
      state.numberSMS = formatPhoneSMS(payload)
      state.groupedSMS = groupSMSByContactNumber(payload)
    },
    setUserSMSLoaded: (state, { payload }: PayloadAction<boolean>) => {
      state.userSMSLoaded = payload
    },

    setContactsSMS: (state, { payload }: PayloadAction<Array<any>>) => {
      state.contactSMS = formatContactSMS(payload)
      state.contactAverageChar = getAverage(payload)
    },
    setContactsSMSLoaded: (state, { payload }: PayloadAction<boolean>) => {
      state.contactSMSLoaded = payload
    },

    setUserCommunicationsVolume: (
      //uses userId to query
      state,
      { payload }: PayloadAction<Array<any>>
    ) => {
      state.userCommunicationsVolume = formatCommunicationsChartData(payload)
    },
    setUserCommunicationsVolumeLoaded: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.userCommunicationsVolumeLoaded = payload
    },

    setAccountCommunicationsVolume: (
      // uses accountId to query
      state,
      { payload }: PayloadAction<Array<any>>
    ) => {
      state.accountCommunicationsVolume = formatCommunicationsChartData(payload)
    },
    setAccountCommunicationsVolumeLoaded: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.accountCommunicationsVolumeLoaded = payload
    },

    setAccountCallStatistics: (state, { payload }: PayloadAction<any>) => {
      const total = {
        totalTime: { hours: 0, minutes: 0, seconds: 0 },
        calls: payload.data.length,
        inbound: 0,
        outbound: 0,
        abandoned: 0,
        missed: 0,
        desktop: 0,
        mobile: 0
      }

      let totalDuration = 0
      let shortest = Infinity
      let longest = -Infinity

      const phoneNumberMap: {
        [number: string]: { count: number; totalTime: number }
      } = {}
      let topNumber: {
        number: string
        totalCalls: number
        totalTime: Duration
      } = {
        number: '',
        totalCalls: 0,
        totalTime: { hours: 0, minutes: 0, seconds: 0 }
      }
      payload.data.forEach((call) => {
        const duration = Number(call['measure_value::double'])

        const internalNumber =
          call.direction === 'outbound' ? call.callerNumber : call.calledNumber
        if (!phoneNumberMap[internalNumber]) {
          phoneNumberMap[internalNumber] = { count: 0, totalTime: 0 }
        }
        phoneNumberMap[internalNumber].count++
        phoneNumberMap[internalNumber].totalTime += duration
        if (topNumber.totalCalls < phoneNumberMap[internalNumber].count) {
          topNumber = {
            number: internalNumber,
            totalCalls: phoneNumberMap[internalNumber].count,
            totalTime: splitMinutes(phoneNumberMap[internalNumber].totalTime)
          }
        }

        totalDuration += duration
        shortest = Math.min(duration, shortest)
        longest = Math.max(duration, longest)

        total.inbound =
          call.direction === 'inbound' ? total.inbound + 1 : total.inbound
        total.outbound =
          call.direction === 'outbound' ? total.outbound + 1 : total.outbound
        total.abandoned =
          call.endingReason === 'abandoned'
            ? total.abandoned + 1
            : total.abandoned
        total.missed =
          call.endingReason === 'missed' ? total.missed + 1 : total.missed
        total.desktop =
          call.pickUpDeviceType === 'webPhone'
            ? total.desktop + 1
            : total.desktop
        total.mobile =
          call.pickUpDeviceType === 'mobileApp'
            ? total.mobile + 1
            : total.mobile
      })

      const averageDuration = totalDuration / payload.data.length

      state.accountCallStatistics = { ...payload, counts: total }
      state.accountCallStatistics.callDuration = {
        total: splitMinutes(totalDuration / 60),
        average: splitMinutes(averageDuration / 60),
        longest: splitMinutes(longest / 60),
        shortest: splitMinutes(shortest / 60)
      }
      state.accountCallStatistics.topNumber = topNumber
      state.accountCallStatistics.topContacts = formatTopContacts(payload.data)
      state.accountCallStatisticsDifference = calculateStatDifference(
        state.accountCallStatistics.counts,
        state.priorIntervalAccountCallStatistics.total
      )
      state.accountCallStatistics.lineChartSeries = formatLineChartSeries(
        payload.data,
        payload.start,
        payload.end
      )
      state.accountCallStatistics.pieChart = {
        devices: {
          categories: ['Mobile', 'Desktop', 'Missed'],
          series: [total.mobile, total.desktop, total.missed]
        },
        external: formatPieChart(payload.data, 'external'),
        internal: formatPieChart(payload.data, 'internal')
      }
    },

    setTodaysAccountCallStatistics: (
      state,
      { payload }: PayloadAction<any>
    ) => {
      state.todaysAccountCallStatistics = payload
    },

    setMonthlyAccountCallStatistics: (
      state,
      { payload }: PayloadAction<any>
    ) => {
      state.monthlyAccountCallStatistics = payload

      state.monthlyAccountCallStatistics.heatMapSeries = formatHeatMapSeries(
        payload.data,
        DateTime.fromMillis(parseInt(payload.start))
      )
    },

    setPriorIntervalAccountCallStatistics: (
      state,
      {
        payload
      }: PayloadAction<{
        loading: boolean
        total: {
          calls: number
          inbound: number
          outbound: number
          abandoned: number
          missed: number
        }
        data: Array<CallSummary>
      }>
    ) => {
      const total = {
        calls: payload.data.length,
        inbound: 0,
        outbound: 0,
        abandoned: 0,
        missed: 0,
        desktop: 0,
        mobile: 0
      }

      payload.data.forEach((call) => {
        total.inbound =
          call.direction === 'inbound' ? total.inbound + 1 : total.inbound
        total.outbound =
          call.direction === 'outbound' ? total.outbound + 1 : total.outbound
        total.abandoned =
          call.endingReason === 'abandoned'
            ? total.abandoned + 1
            : total.abandoned
        total.missed =
          call.endingReason === 'missed' ? total.missed + 1 : total.missed
        total.desktop =
          call.pickUpDeviceType === 'webPhone'
            ? total.desktop + 1
            : total.desktop
        total.mobile =
          call.pickUpDeviceType === 'mobileApp'
            ? total.mobile + 1
            : total.mobile
      })

      state.priorIntervalAccountCallStatistics = { ...payload, total }
      state.accountCallStatisticsDifference = calculateStatDifference(
        state.accountCallStatistics.counts,
        state.priorIntervalAccountCallStatistics.total
      )
    },

    setNumberFilter: (state, { payload }) => {
      state.numberFilter = payload
    }
  }
})

export const {
  setLoading,
  setErrors,
  setUserSMS,
  setUserSMSLoaded,
  setContactsSMS,
  setContactsSMSLoaded,
  setUserCommunicationsVolume,
  setUserCommunicationsVolumeLoaded,
  setAccountCommunicationsVolume,
  setAccountCommunicationsVolumeLoaded,
  setAccountCallStatistics,
  setTodaysAccountCallStatistics,
  setMonthlyAccountCallStatistics,
  setPriorIntervalAccountCallStatistics,
  setNumberFilter
} = analyticsSlice.actions
export default analyticsSlice.reducer
export const analyticsSelector = (state: { analyticsStore: AnalyticsState }) =>
  state.analyticsStore

//for Admins only
export const getSMSDataByUser = (
  token: string,
  startTime: string,
  endTime: string,
  accountId: string,
  numbers?: Array<string>
): AppThunk => {
  return async (dispatch) => {
    dispatch(setUserSMSLoaded(false))
    try {
      const params = new URLSearchParams()
      params.append('startTime', startTime)
      params.append('endTime', endTime)
      params.append('accountId', accountId)

      if (numbers?.length) {
        numbers.forEach((number) => {
          params.append('numbers', number.replace('+', ''))
        })
      }

      const result = await HTTP.get('/metrics/sms', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      })
      const { data } = result
      const SMSData: SMSDataInterface[] = parseData(data.rows)

      dispatch(setUserSMS(SMSData))
      dispatch(setUserSMSLoaded(true))
    } catch (error) {
      console.error(error)
    }
  }
}

//for admins and users
export const getSMSDataByContacts = (
  token: string,
  startTime: string,
  endTime: string,
  australisId: string,
  numbers?: Array<string>
): AppThunk => {
  return async (dispatch) => {
    dispatch(setContactsSMSLoaded(false))

    const params = new URLSearchParams()
    params.append('startTime', startTime)
    params.append('endTime', endTime)
    params.append('australisId', australisId)

    if (numbers?.length) {
      numbers.forEach((number) => {
        params.append('numbers', number.replace('+', ''))
      })
    }

    try {
      const result = await HTTP.get('/metrics/sms', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      })
      const { data } = result
      const SMSData: SMSDataInterface[] = parseData(data.rows)

      dispatch(setContactsSMS(SMSData))
      dispatch(setContactsSMSLoaded(true))
    } catch (error) {
      console.error(error)
    }
  }
}

export const getUserCommunicationsVolume = (
  token: string,
  startTime: string,
  endTime: string,
  australisId: string,
  interval: string
): AppThunk => {
  return async (dispatch) => {
    dispatch(setUserCommunicationsVolumeLoaded(false))
    try {
      const result = await HTTP.get('/metrics/sms', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          startTime,
          endTime,
          australisId,
          interval
        }
      })
      const { data } = result
      const SMSUserVolume: UserCommunicationsVolume[] = parseData(data.rows)

      dispatch(setUserCommunicationsVolume(SMSUserVolume))
      dispatch(setUserCommunicationsVolumeLoaded(true))
    } catch (error) {
      console.error(error)
    }
  }
}

export const getAccountCommunicationsVolume = (
  token: string,
  startTime: string,
  endTime: string,
  accountId: string,
  interval: string,
  numbers?: Array<string>
): AppThunk => {
  return async (dispatch) => {
    dispatch(setAccountCommunicationsVolumeLoaded(false))

    const params = new URLSearchParams()
    params.append('startTime', startTime)
    params.append('endTime', endTime)
    params.append('accountId', accountId)
    params.append('interval', interval)

    if (numbers?.length) {
      numbers.forEach((number) => {
        params.append('numbers', number.replace('+', ''))
      })
    }

    try {
      const result = await HTTP.get('/metrics/sms', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      })
      const { data } = result
      const SMSAccountVolume: UserCommunicationsVolume[] = parseData(data.rows)

      dispatch(setAccountCommunicationsVolume(SMSAccountVolume))
      dispatch(setAccountCommunicationsVolumeLoaded(true))
    } catch (error) {
      console.error(error)
    }
  }
}

export const applyNumberFilter = (numbers: string[]) => {
  setNumberFilter(numbers)
}

export const getAccountCallStatistics = (
  token: string,
  accountId: string,
  startTime: string,
  endTime: string,
  numbers?: Array<string>,
  order?: 'ASC' | 'DESC',
  limit?: number
): AppThunk => {
  return async (dispatch, getState) => {
    const { accountCallStatistics } = getState().analytics

    if (
      accountCallStatistics.start === startTime &&
      accountCallStatistics.end === endTime
    ) {
      return
    }
    dispatch(
      setAccountCallStatistics({ ...accountCallStatistics, loading: true })
    )

    const params = new URLSearchParams()
    params.append('startTime', startTime)
    params.append('endTime', endTime)
    params.append('accountId', accountId)

    if (numbers?.length) {
      numbers.forEach((number) => {
        params.append('numbers', number.replace('+', ''))
      })
    }

    try {
      const result = await HTTP.get('/metrics/calls', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      })

      const { data } = result
      const callStatistics: Array<CallSummary> = parseData(data.rows)

      dispatch(
        setAccountCallStatistics({
          ...accountCallStatistics,
          loading: false,
          start: startTime,
          end: endTime,
          data: callStatistics
        })
      )
    } catch (error) {
      dispatch(
        setAccountCallStatistics({ ...accountCallStatistics, loading: false })
      )
      console.error(error)
    }
  }
}

export const getPriorIntervalAccountCallStatistics = (
  token: string,
  accountId: string,
  startTime: string,
  endTime: string,
  numbers?: Array<string>,
  order?: 'ASC' | 'DESC',
  limit?: number
): AppThunk => {
  return async (dispatch, getState) => {
    const { priorIntervalAccountCallStatistics } = getState().analytics
    dispatch(
      setPriorIntervalAccountCallStatistics({
        ...priorIntervalAccountCallStatistics,
        loading: true
      })
    )

    const params = new URLSearchParams()
    params.append('startTime', startTime)
    params.append('endTime', endTime)
    params.append('accountId', accountId)

    if (numbers?.length) {
      numbers.forEach((number) => {
        params.append('numbers', number.replace('+', ''))
      })
    }

    try {
      const result = await HTTP.get('/metrics/calls', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      })

      const { data } = result
      const callStatistics: Array<CallSummary> = parseData(data.rows)

      dispatch(
        setPriorIntervalAccountCallStatistics({
          ...priorIntervalAccountCallStatistics,
          loading: false,
          data: callStatistics
        })
      )
    } catch (error) {
      dispatch(
        setPriorIntervalAccountCallStatistics({
          ...priorIntervalAccountCallStatistics,
          loading: false
        })
      )
      console.error(error)
    }
  }
}

export const getTodaysAccountCallStatistics = (
  token: string,
  accountId: string,
  numbers?: Array<string>,
  order?: 'ASC' | 'DESC',
  limit?: number
): AppThunk => {
  return async (dispatch, getState) => {
    const { todaysAccountCallStatistics } = getState().analytics

    const todayStart = DateTime.local().startOf('day').toMillis().toString()
    const todayEnd = DateTime.local().endOf('day').toMillis().toString()

    dispatch(
      setTodaysAccountCallStatistics({
        ...todaysAccountCallStatistics,
        loading: true
      })
    )

    const params = new URLSearchParams()
    params.append('startTime', todayStart)
    params.append('endTime', todayEnd)
    params.append('accountId', accountId)

    if (numbers?.length) {
      numbers.forEach((number) => {
        params.append('numbers', number.replace('+', ''))
      })
    }

    try {
      const result = await HTTP.get('/metrics/calls', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      })

      const { data } = result
      const callStatistics: Array<CallSummary> = parseData(data.rows)

      let busiestHour = { hourString: '', total: 0 }
      let numCallsByHour = {}
      callStatistics.forEach((row) => {
        const date = DateTime.fromSQL(row.time, { zone: 'utc' }).toLocal()
        const dateHourString = date.toLocaleString({ hour: 'numeric' })
        const rowHour = date.hour
        if (!numCallsByHour[rowHour]) {
          numCallsByHour[rowHour] = 0
        }
        numCallsByHour[rowHour]++

        if (numCallsByHour[rowHour] > busiestHour.total) {
          busiestHour = {
            hourString: dateHourString,
            total: numCallsByHour[rowHour]
          }
        }
      })

      dispatch(
        setTodaysAccountCallStatistics({
          ...todaysAccountCallStatistics,
          data: callStatistics,
          busiestHour: busiestHour.hourString,
          start: todayStart,
          end: todayEnd
        })
      )
    } catch (error) {
      console.error(error)
    }
  }
}

export const getMonthlyAccountCallStatistics = (
  token: string,
  accountId: string,
  startTime: string,
  endTime: string,
  numbers?: Array<string>,
  order?: 'ASC' | 'DESC',
  limit?: number
): AppThunk => {
  return async (dispatch, getState) => {
    const { monthlyAccountCallStatistics } = getState().analytics

    if (startTime === monthlyAccountCallStatistics.start) {
      return
    }

    dispatch(
      setMonthlyAccountCallStatistics({
        ...monthlyAccountCallStatistics,
        loading: true
      })
    )

    const params = new URLSearchParams()
    params.append('startTime', startTime)
    params.append('endTime', endTime)
    params.append('accountId', accountId)

    if (numbers?.length) {
      numbers.forEach((number) => {
        params.append('numbers', number.replace('+', ''))
      })
    }

    try {
      const result = await HTTP.get('/metrics/calls', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      })

      const { data } = result
      const callStatistics: Array<CallSummary> = parseData(data.rows)

      dispatch(
        setMonthlyAccountCallStatistics({
          ...monthlyAccountCallStatistics,
          loading: false,
          data: callStatistics,
          start: startTime,
          end: endTime
        })
      )
    } catch (error) {
      dispatch(
        setMonthlyAccountCallStatistics({
          ...monthlyAccountCallStatistics,
          loading: false
        })
      )
      console.error(error)
    }
  }
}
